// src/services/suppliersService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class SuppliersService {
  
  // 🏢 Get all suppliers with pagination support
  async getAllSuppliers(params = {}) {
    try {
      console.log('🏢 Fetching all suppliers with params:', params)
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.GET_ALL, params)
      console.log('✅ Suppliers fetched successfully:', response)
      
      // Calculate actual purchase totals from GRN data
      if (response?.data?.suppliers) {
        const suppliersWithTotals = await this.calculateSupplierPurchaseTotals(response.data.suppliers)
        return {
          ...response,
          data: {
            ...response.data,
            suppliers: suppliersWithTotals
          }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error)
      throw error
    }
  }

  // 🏢 Get supplier by ID
  async getSupplierById(id) {
    try {
      console.log('🏢 Fetching supplier by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Supplier fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching supplier:', error)
      throw error
    }
  }

  // 🏢 Create new supplier
  async createSupplier(supplierData) {
    try {
      console.log('🏢 Creating new supplier with data:', supplierData)
      
      // Validate required fields
      this.validateSupplierData(supplierData)
      
      const response = await ApiService.post(AppURLs.SUPPLIERS.CREATE, supplierData)
      console.log('✅ Supplier created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating supplier:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate data)
        const conflictError = new Error(error.response.data?.message || 'Supplier already exists')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 🏢 Update supplier using PATCH
  async updateSupplier(id, supplierData) {
    try {
      console.log('🏢 Updating supplier:', { id, supplierData })
      
      // Validate partial data for updates
      this.validatePartialSupplierData(supplierData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, supplierData)
      console.log('✅ Supplier updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating supplier:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate data)
        const conflictError = new Error(error.response.data?.message || 'Supplier data conflicts with existing record')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Supplier not found')
        notFoundError.response = error.response
        throw notFoundError
      }
      
      throw error
    }
  }

  // 🏢 Delete supplier
  async deleteSupplier(id) {
    try {
      console.log('🏢 Deleting supplier with ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Supplier deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting supplier:', error)
      
      // Enhanced error handling for delete operations
      if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Supplier not found')
        notFoundError.response = error.response
        throw notFoundError
      } else if (error.response?.status === 409) {
        // Conflict error (cannot delete due to dependencies)
        const conflictError = new Error(error.response.data?.message || 'Cannot delete supplier - it may have associated records')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 403) {
        // Forbidden error
        const forbiddenError = new Error('You do not have permission to delete this supplier')
        forbiddenError.response = error.response
        throw forbiddenError
      }
      
      throw error
    }
  }

  // 🏢 Search suppliers
  async searchSuppliers(query, params = {}) {
    try {
      console.log('🏢 Searching suppliers with query:', query)
      const searchParams = { ...params, q: query }
      const response = await ApiService.get(AppURLs.SUPPLIERS.SEARCH, searchParams)
      console.log('✅ Supplier search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching suppliers:', error)
      throw error
    }
  }

  // 🏢 Validate supplier name
  async validateName(name, excludeId = null) {
    try {
      console.log('🏢 Validating supplier name:', name)
      const params = { name }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_NAME, params)
      console.log('✅ Name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier name:', error)
      throw error
    }
  }

  // 🏢 Validate mobile number
  async validateMobile(mobile, excludeId = null) {
    try {
      console.log('🏢 Validating supplier mobile:', mobile)
      const params = { mobile }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_MOBILE, params)
      console.log('✅ Mobile validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier mobile:', error)
      throw error
    }
  }

  // 🏢 Validate email
  async validateEmail(email, excludeId = null) {
    try {
      console.log('🏢 Validating supplier email:', email)
      const params = { email }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_EMAIL, params)
      console.log('✅ Email validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier email:', error)
      throw error
    }
  }

  // 🏢 Bulk delete suppliers
  async bulkDelete(ids) {
    try {
      console.log('🏢 Bulk deleting suppliers with IDs:', ids)
      const response = await ApiService.post(AppURLs.SUPPLIERS.BULK_DELETE, { ids })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk delete:', error)
      throw error
    }
  }

  // 🏢 Bulk status update
  async bulkUpdateStatus(ids, isActive) {
    try {
      console.log('🏢 Bulk updating supplier status:', { ids, isActive })
      const response = await ApiService.post(AppURLs.SUPPLIERS.BULK_STATUS, { ids, isActive })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk status update:', error)
      throw error
    }
  }

  // 🏢 Get suppliers statistics
  async getStats() {
    try {
      console.log('🏢 Fetching supplier statistics')
      const response = await ApiService.get(AppURLs.SUPPLIERS.STATS)
      console.log('✅ Supplier stats fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching supplier stats:', error)
      throw error
    }
  }

  // 🔒 Validation Methods

  // Validate supplier data for creation
  validateSupplierData(data) {
    const errors = []

    // Required fields validation
    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push('Supplier name is required and must be a non-empty string')
    }

    if (!data.mobile || typeof data.mobile !== 'string' || data.mobile.trim().length === 0) {
      errors.push('Mobile number is required and must be a non-empty string')
    }

    if (!data.contactPerson || typeof data.contactPerson !== 'string' || data.contactPerson.trim().length === 0) {
      errors.push('Contact person is required and must be a non-empty string')
    }

    // Email validation (optional but must be valid if provided)
    if (data.email && data.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.push('Email must be a valid email address')
      }
    }

    // Mobile number format validation
    if (data.mobile) {
      const mobileRegex = /^[0-9+\-\s()]+$/
      if (!mobileRegex.test(data.mobile)) {
        errors.push('Mobile number must contain only numbers, +, -, spaces, and parentheses')
      }
    }

    // Account information validation (optional but must be complete if provided)
    if (data.accountInformation) {
      const { accountName, accountNumber, bankName, branchName } = data.accountInformation
      
      if (accountName || accountNumber || bankName || branchName) {
        if (!accountName || accountName.trim().length === 0) {
          errors.push('Account name is required when account information is provided')
        }
        if (!accountNumber || accountNumber.trim().length === 0) {
          errors.push('Account number is required when account information is provided')
        }
        if (!bankName || bankName.trim().length === 0) {
          errors.push('Bank name is required when account information is provided')
        }
        if (!branchName || branchName.trim().length === 0) {
          errors.push('Branch name is required when account information is provided')
        }
      }
    }

    // String length validations
    if (data.name && data.name.length > 100) {
      errors.push('Supplier name must not exceed 100 characters')
    }

    if (data.mobile && data.mobile.length > 20) {
      errors.push('Mobile number must not exceed 20 characters')
    }

    if (data.contactPerson && data.contactPerson.length > 100) {
      errors.push('Contact person name must not exceed 100 characters')
    }

    if (data.email && data.email.length > 100) {
      errors.push('Email must not exceed 100 characters')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // Validate partial supplier data for updates
  validatePartialSupplierData(data) {
    const errors = []

    // Optional fields validation (only validate if provided)
    if (data.name !== undefined) {
      if (typeof data.name !== 'string' || data.name.trim().length === 0) {
        errors.push('Supplier name must be a non-empty string')
      }
      if (data.name.length > 100) {
        errors.push('Supplier name must not exceed 100 characters')
      }
    }

    if (data.mobile !== undefined) {
      if (typeof data.mobile !== 'string' || data.mobile.trim().length === 0) {
        errors.push('Mobile number must be a non-empty string')
      }
      if (data.mobile.length > 20) {
        errors.push('Mobile number must not exceed 20 characters')
      }
      const mobileRegex = /^[0-9+\-\s()]+$/
      if (!mobileRegex.test(data.mobile)) {
        errors.push('Mobile number must contain only numbers, +, -, spaces, and parentheses')
      }
    }

    if (data.contactPerson !== undefined) {
      if (typeof data.contactPerson !== 'string' || data.contactPerson.trim().length === 0) {
        errors.push('Contact person must be a non-empty string')
      }
      if (data.contactPerson.length > 100) {
        errors.push('Contact person name must not exceed 100 characters')
      }
    }

    if (data.email !== undefined && data.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.push('Email must be a valid email address')
      }
      if (data.email.length > 100) {
        errors.push('Email must not exceed 100 characters')
      }
    }

    if (data.isActive !== undefined && typeof data.isActive !== 'boolean') {
      errors.push('isActive must be a boolean value')
    }

    // Account information validation for updates
    if (data.accountInformation !== undefined) {
      const { accountName, accountNumber, bankName, branchName } = data.accountInformation
      
      if (accountName || accountNumber || bankName || branchName) {
        if (!accountName || accountName.trim().length === 0) {
          errors.push('Account name is required when account information is provided')
        }
        if (!accountNumber || accountNumber.trim().length === 0) {
          errors.push('Account number is required when account information is provided')
        }
        if (!bankName || bankName.trim().length === 0) {
          errors.push('Bank name is required when account information is provided')
        }
        if (!branchName || branchName.trim().length === 0) {
          errors.push('Branch name is required when account information is provided')
        }
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // 🎯 Utility Methods

  // Format supplier data for display
  formatSupplierForDisplay(supplier) {
    if (!supplier) return null

    return {
      id: supplier._id,
      name: supplier.name,
      mobile: supplier.mobile,
      contactPerson: supplier.contactPerson,
      email: supplier.email || 'N/A',
      address: supplier.address || 'N/A',
      accountInformation: supplier.accountInformation || {},
      totalPurchases: supplier.totalPurchases || 0,
      totalPaid: supplier.totalPaid || 0,
      balanceDue: supplier.balanceDue || 0,
      isActive: supplier.isActive !== false,
      createdAt: supplier.createdAt,
      updatedAt: supplier.updatedAt,
      createdBy: supplier.createdBy?.username || 'Unknown'
    }
  }

  // Format currency for display
  formatCurrency(amount) {
    if (typeof amount === 'number') {
      return `Rs. ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    return 'Rs. 0.00'
  }

  // Calculate balance due
  calculateBalanceDue(totalPurchases, totalPaid) {
    const purchases = parseFloat(totalPurchases) || 0
    const paid = parseFloat(totalPaid) || 0
    return purchases - paid
  }

  // Validate phone number format
  isValidPhoneNumber(phone) {
    if (!phone) return false
    const phoneRegex = /^[0-9+\-\s()]+$/
    return phoneRegex.test(phone) && phone.trim().length >= 8 && phone.trim().length <= 20
  }

  // Validate email format
  isValidEmail(email) {
    if (!email) return true // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Get supplier status badge color
  getStatusBadgeColor(isActive) {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
  }

  // Search suppliers locally (client-side filtering)
  searchSuppliersLocally(suppliers, searchTerm) {
    if (!searchTerm || !Array.isArray(suppliers)) return suppliers
    
    const lowercaseSearch = searchTerm.toLowerCase()
    return suppliers.filter(supplier => {
      const name = supplier.name?.toLowerCase() || ''
      const mobile = supplier.mobile?.toLowerCase() || ''
      const email = supplier.email?.toLowerCase() || ''
      const contactPerson = supplier.contactPerson?.toLowerCase() || ''
      
      return name.includes(lowercaseSearch) || 
             mobile.includes(lowercaseSearch) || 
             email.includes(lowercaseSearch) ||
             contactPerson.includes(lowercaseSearch)
    })
  }

  // Filter suppliers by status
  filterSuppliersByStatus(suppliers, status) {
    if (!Array.isArray(suppliers)) return []
    if (status === 'all') return suppliers
    
    const isActive = status === 'active'
    return suppliers.filter(supplier => supplier.isActive === isActive)
  }

  // Get financial data for a specific supplier directly from GRN payments endpoint
  async getSupplierFinancialData(supplierId) {
    try {
      console.log('💰 Fetching supplier financial data from GRN payments for supplier:', supplierId)
      
      // Fetch GRN payments directly from the backend endpoint
      const endpoint = 'https://dev-backend.tezlaa.com/api/v1/grn-payments'
      console.log('💰 Calling GRN payments endpoint:', endpoint)
      
      const grnPaymentsResponse = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header if needed
          ...(localStorage.getItem('tezlaa-access-token') && {
            'Authorization': `Bearer ${localStorage.getItem('tezlaa-access-token')}`
          })
        }
      })
      
      if (!grnPaymentsResponse.ok) {
        throw new Error(`HTTP error! status: ${grnPaymentsResponse.status}`)
      }
      
      const grnPaymentsData = await grnPaymentsResponse.json()
      const payments = grnPaymentsData?.data?.payments || grnPaymentsData?.data?.grnPayments || grnPaymentsData?.data || grnPaymentsData || []
      
      console.log('💰 GRN payments response:', {
        success: grnPaymentsData?.success,
        totalPayments: payments.length,
        samplePayment: payments[0] ? {
          id: payments[0]._id,
          amountPaid: payments[0].amountPaid,
          grnNumber: payments[0].grn?.grnNumber,
          grnTotalAmount: payments[0].grn?.totalAmount,
          supplierFromGRN: payments[0].grn?.purchaseOrder?.supplier?.name
        } : null
      })
      
      console.log(`� Looking for payments for supplier: ${supplierId} (looking through ${payments.length} payments)`)
      
      // Filter payments for this supplier using the correct path: grn.purchaseOrder.supplier._id
      let totalPaid = 0
      let totalPurchases = 0
      let paymentCount = 0
      const supplierPayments = []
      const processedGRNs = new Set() // Track unique GRNs to avoid double counting
      
      payments.forEach(payment => {
        // Primary matching strategy: grn.purchaseOrder.supplier._id (based on API response structure)
        const purchaseOrderSupplierMatch = payment.grn?.purchaseOrder?.supplier?._id === supplierId ||
                                          payment.grn?.purchaseOrder?.supplier?._id?.toString() === supplierId?.toString()
        
        // Fallback matching strategies
        const directSupplierMatch = payment.supplier?._id === supplierId || payment.supplierId === supplierId
        const grnSupplierMatch = payment.grn?.supplier?._id === supplierId
        
        const isSupplierMatch = purchaseOrderSupplierMatch || directSupplierMatch || grnSupplierMatch
        
        if (isSupplierMatch) {
          const paidAmount = parseFloat(payment.amountPaid || 0)
          const grnTotalAmount = parseFloat(payment.grn?.totalAmount || 0) 
          const grnNumber = payment.grn?.grnNumber || 'N/A'
          
          totalPaid += paidAmount
          paymentCount += 1
          
          // Only count GRN total amount once per unique GRN
          if (!processedGRNs.has(grnNumber) && grnTotalAmount > 0) {
            totalPurchases += grnTotalAmount
            processedGRNs.add(grnNumber)
          }
          
          supplierPayments.push({
            paymentId: payment._id,
            amount: paidAmount,
            paymentDate: payment.paymentDate || payment.createdAt,
            grnNumber: grnNumber,
            paymentMethod: payment.paymentType || payment.paymentMethod || 'cash',
            grnTotalAmount: grnTotalAmount
          })
          
          console.log('💰 Found matching payment:', {
            paymentId: payment._id,
            supplierName: payment.grn?.purchaseOrder?.supplier?.name,
            paidAmount: paidAmount,
            grnTotalAmount: grnTotalAmount,
            grnNumber: grnNumber,
            matchType: purchaseOrderSupplierMatch ? 'purchaseOrder' : directSupplierMatch ? 'direct' : 'grnSupplier'
          })
        }
      })
      
      console.log(`📊 Supplier totals calculated: Purchases=${totalPurchases}, Paid=${totalPaid}, GRNs=${processedGRNs.size}, Payments=${paymentCount}`)
      
      const balanceDue = totalPurchases - totalPaid
      
      // Determine payment status
      let paymentStatus = 'No Transactions'
      if (totalPurchases > 0) {
        if (balanceDue <= 0) {
          paymentStatus = 'Fully Paid'
        } else if (totalPaid > 0) {
          paymentStatus = 'Partially Paid'
        } else {
          paymentStatus = 'Unpaid'
        }
      }
      
      const financialData = {
        totalPurchases,
        totalPaid,
        balanceDue,
        paymentStatus,
        grnCount: paymentCount,
        paymentDetails: supplierPayments
      }
      
      console.log('✅ Supplier financial data calculated:', financialData)
      return { success: true, data: financialData }
      
    } catch (error) {
      console.error('❌ Error fetching supplier financial data:', error)
      // Return default values if calculation fails
      return {
        success: false,
        data: {
          totalPurchases: 0,
          totalPaid: 0,
          balanceDue: 0,
          paymentStatus: 'No Data',
          grnCount: 0,
          paymentDetails: []
        },
        error: error.message
      }
    }
  }

  // Calculate supplier purchase totals from GRN data
  async calculateSupplierPurchaseTotals(suppliers) {
    try {
      console.log('🏢 Calculating supplier purchase totals from GRN data...')
      console.log(`📋 Processing ${suppliers?.length || 0} suppliers`)
      
      if (!Array.isArray(suppliers) || suppliers.length === 0) {
        console.log('⚠️ No suppliers to process')
        return suppliers
      }

      // Get all GRN payments data instead of GRN data
      console.log('� Fetching GRN payments data for supplier totals calculation...')
      
      const endpoint = 'https://dev-backend.tezlaa.com/api/v1/grn-payments'
      console.log('� Using GRN payments endpoint:', endpoint)
      
      const grnPaymentsResponse = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('tezlaa-access-token') && {
            'Authorization': `Bearer ${localStorage.getItem('tezlaa-access-token')}`
          })
        }
      })
      
      if (!grnPaymentsResponse.ok) {
        console.warn('⚠️ Failed to fetch GRN payments data:', grnPaymentsResponse.status)
        return suppliers.map(supplier => ({
          ...supplier,
          totalPurchases: 0,
          grnCount: 0
        }))
      }
      
      const grnPaymentsData = await grnPaymentsResponse.json()
      const payments = grnPaymentsData?.data?.payments || grnPaymentsData?.data?.grnPayments || grnPaymentsData?.data || grnPaymentsData || []
      
      console.log('💰 GRN payments response for totals:', {
        success: !!grnPaymentsData,
        paymentsCount: payments.length
      })
      
      if (!Array.isArray(payments) || payments.length === 0) {
        console.warn('⚠️ No GRN payments data available for purchase calculations')
        return suppliers.map(supplier => ({
          ...supplier,
          totalPurchases: 0,
          grnCount: 0
        }))
      }

      console.log(`� Found ${payments.length} payment records`)
      
      // Log sample payment structure
      if (payments.length > 0) {
        console.log('� Sample payment structure:', {
          id: payments[0]._id,
          supplierId: payments[0].supplierId,
          supplier: payments[0].supplier,
          grnSupplier: payments[0].grn?.supplier,
          purchaseOrderSupplier: payments[0].grn?.purchaseOrder?.supplier,
          amountPaid: payments[0].amountPaid,
          grnTotalAmount: payments[0].grn?.totalAmount,
          keys: Object.keys(payments[0])
        })
      }

      // Log all supplier IDs from payments for debugging
      const paymentSupplierIds = payments.map(payment => ({
        paymentId: payment._id,
        supplierId: payment.supplierId,
        supplier: payment.supplier,
        grnSupplier: payment.grn?.supplier,
        purchaseOrderSupplier: payment.grn?.purchaseOrder?.supplier,
        amountPaid: payment.amountPaid,
        grnTotalAmount: payment.grn?.totalAmount
      }))
      console.log('� All payment supplier mappings:', paymentSupplierIds)

      // Log supplier IDs for matching
      console.log('🏢 Supplier IDs to match:', suppliers.map(s => ({ name: s.name, id: s._id })))

      // Calculate total purchases for each supplier from payments
      const suppliersWithTotals = suppliers.map(supplier => {
        console.log(`🔍 Matching payments for supplier: ${supplier.name} (${supplier._id})`)
        
        const supplierPayments = payments.filter(payment => {
          const directMatch = payment.supplierId === supplier._id
          const supplierObjMatch = payment.supplier?._id === supplier._id || payment.supplier?.id === supplier._id
          const stringMatch = payment.supplierId?.toString() === supplier._id?.toString()
          const grnSupplierMatch = payment.grn?.supplier?._id === supplier._id || payment.grn?.supplier?.id === supplier._id
          const poSupplierMatch = payment.grn?.purchaseOrder?.supplier?._id === supplier._id || payment.grn?.purchaseOrder?.supplier?.id === supplier._id
          const poSupplierStringMatch = payment.grn?.purchaseOrder?.supplier?.toString() === supplier._id?.toString()
          
          const matches = directMatch || supplierObjMatch || stringMatch || grnSupplierMatch || poSupplierMatch || poSupplierStringMatch
          
          if (matches) {
            console.log(`✅ Payment ${payment._id} matches supplier ${supplier.name} (Amount: Rs. ${payment.amountPaid}, GRN Total: Rs. ${payment.grn?.totalAmount})`)
            console.log(`   Match type: ${directMatch ? 'direct' : supplierObjMatch ? 'supplierObj' : stringMatch ? 'string' : grnSupplierMatch ? 'grnSupplier' : poSupplierMatch ? 'poSupplier' : 'poSupplierString'}`)
          }
          
          return matches
        })
        
          // Calculate totals avoiding double-counting GRNs
        const processedGRNs = new Set()
        let totalPurchases = 0
        let totalPaid = 0
        
        supplierPayments.forEach(payment => {
          // Add payment amount
          totalPaid += parseFloat(payment.amountPaid || 0)
          
          // Add GRN total only once per unique GRN
          const grnNumber = payment.grn?.grnNumber
          const grnAmount = parseFloat(payment.grn?.totalAmount || 0)
          if (grnNumber && !processedGRNs.has(grnNumber) && grnAmount > 0) {
            totalPurchases += grnAmount
            processedGRNs.add(grnNumber)
          }
        })

        console.log(`� Supplier "${supplier.name}" (${supplier._id}): ${supplierPayments.length} payments, Total Purchases: Rs. ${totalPurchases.toFixed(2)}, Total Paid: Rs. ${totalPaid.toFixed(2)}`)

        return {
          ...supplier,
          totalPurchases: totalPurchases,
          totalPaid: totalPaid,
          grnCount: supplierPayments.length
        }
      })

      const totalPurchases = suppliersWithTotals.reduce((sum, s) => sum + (s.totalPurchases || 0), 0)
      const totalPaid = suppliersWithTotals.reduce((sum, s) => sum + (s.totalPaid || 0), 0)
      console.log(`✅ Supplier totals calculated from payments - Total Purchases: Rs. ${totalPurchases.toFixed(2)}, Total Paid: Rs. ${totalPaid.toFixed(2)}`)
      return suppliersWithTotals

    } catch (error) {
      console.error('❌ Error calculating supplier purchase totals:', error)
      console.error('❌ Error details:', error.message)
      // Return original suppliers with default totals if calculation fails
      return suppliers.map(supplier => ({
        ...supplier,
        totalPurchases: supplier.totalPurchases || 0,
        totalPaid: supplier.totalPaid || 0,
        grnCount: 0
      }))
    }
  }
}

export default new SuppliersService()